import React, { Fragment } from "react";
import { Field, FieldArray, FieldProps, Form, FormikProps } from "formik";
import classnames from "classnames";
import { WithTranslation, withTranslation } from "react-i18next";

import { Site } from "models/site/Site";

import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";

import IconClose from "@material-ui/icons/Close";

import Stepper from "components/common/stepper/Stepper";
import Switch from "components/common/form/switch/Switch";

import { CreateApplicationFormValues as FormValues } from "./Create";
import { MenuItem, Select } from "@material-ui/core";

const FormComponent = ({
  formikProps,
  formikProps: { values, errors, touched, isSubmitting, setFieldValue },
  site,
  close,
  t,
  translationPath
}: {
  formikProps: FormikProps<FormValues>;
  site: Site;
  close: (event: any) => void;
  translationPath: string;
} & WithTranslation) => (
  <Form className="d-flex flex-column h-100">
    <div className="d-flex flex-column h-100">
      <div
        className="d-flex py-2 px-3 justify-content-between align-items-center"
        style={{ borderBottom: "1px solid #dadce0" }}
      >
        <IconButton onClick={close}>
          <IconClose fontSize="small" />
        </IconButton>
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          size="small"
          className="p-2"
          disabled={!formikProps.isValid || isSubmitting}
        >
          {t(`${translationPath}.createButton`)}
        </Button>
      </div>
      <div
        className="flex-grow-1 py-4 px-5 overflow-auto"
        style={{ backgroundColor: "rgb(241, 243, 244)" }}
      >
        <Stepper
          titles={t(`${translationPath}.stepper.titles`, {
            returnObjects: true
          })}
          steps={[
            <Fragment>
              <section className="box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.name.label`)}{" "}
                  <small>
                    ({t(`${translationPath}.fields.name.description`)})
                  </small>
                </p>
                <section className="d-flex flex-column">
                  <Field
                    name="name"
                    render={({ field }: FieldProps<FormValues>) => (
                      <TextField
                        {...field}
                        autoFocus
                        inputProps={{
                          className: "px-2"
                        }}
                        className={classnames("bg-white m-0", {
                          "is-valid": !errors.name && touched.name,
                          "is-invalid": errors.name && touched.name
                        })}
                        tabIndex={1}
                      />
                    )}
                  />
                  {errors.name && touched.name && (
                    <div className="d-flex invalid-feedback">
                      {t(`${translationPath}.fields.name.error`)}
                    </div>
                  )}
                </section>
              </section>
              <section className="d-none box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.environment.label`)}
                </p>
                <section className="d-flex flex-column">
                  <Field
                    name="environment"
                    component="select"
                    className="browser-default custom-select"
                    tabIndex={2}
                  >
                    {t<string[]>(
                      `${translationPath}.fields.environment.types`,
                      {
                        returnObjects: true
                      }
                    ).map((environment: string) => (
                      <option key={environment} value={environment}>
                        {environment}
                      </option>
                    ))}
                  </Field>
                </section>
              </section>
              <section className="box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.accountManagerNote.label`)}{" "}
                  <small>
                    (
                    {t(
                      `${translationPath}.fields.accountManagerNote.description`
                    )}
                    )
                  </small>
                </p>
                <section className="d-flex flex-column">
                  <Field
                    name="accountManagerNote"
                    render={({ field }: FieldProps<FormValues>) => (
                      <TextField
                        {...field}
                        multiline
                        inputProps={{
                          className: "px-2"
                        }}
                        className={classnames("bg-white m-0", {
                          "is-valid": !errors.accountManagerNote,
                          "is-invalid":
                            errors.accountManagerNote &&
                            touched.accountManagerNote
                        })}
                        tabIndex={4}
                      />
                    )}
                  />
                  {errors.accountManagerNote && touched.accountManagerNote && (
                    <div className="d-flex invalid-feedback">
                      {t(`${translationPath}.fields.accountManagerNote.error`)}
                    </div>
                  )}
                </section>
              </section>
              <section className="box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.note.label`)}{" "}
                  <small>
                    ({t(`${translationPath}.fields.note.description`)})
                  </small>
                </p>
                <section className="d-flex flex-column">
                  <Field
                    name="note"
                    render={({ field }: FieldProps<FormValues>) => (
                      <TextField
                        {...field}
                        inputProps={{
                          className: "px-2"
                        }}
                        className={classnames("bg-white m-0", {
                          "is-valid": !errors.note,
                          "is-invalid": errors.note && touched.note
                        })}
                        tabIndex={4}
                      />
                    )}
                  />
                  {errors.note && touched.note && (
                    <div className="d-flex invalid-feedback">
                      {t(`${translationPath}.fields.note.error`)}
                    </div>
                  )}
                </section>
              </section>
            </Fragment>,
            <Fragment>
              <section className="box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.mainDomainName.label`)}{" "}
                  {values.name ? (
                    <small>
                      {" "}
                      - (
                      {t(
                        `${translationPath}.fields.mainDomainName.description`
                      )}
                      :{" "}
                      <strong>{`${values.name}.${site.technicalDomain}`}</strong>
                      )
                    </small>
                  ) : null}
                </p>
                <section className="d-flex flex-column">
                  <Field
                    name="mainDomainName"
                    render={({ field }: FieldProps<FormValues>) => (
                      <TextField
                        {...field}
                        inputProps={{
                          className: "px-2"
                        }}
                        className={classnames("bg-white m-0", {
                          "is-valid": !errors.mainDomainName,
                          "is-invalid":
                            errors.mainDomainName && touched.mainDomainName
                        })}
                        tabIndex={3}
                      />
                    )}
                  />
                  {errors.mainDomainName && touched.mainDomainName && (
                    <div className="d-flex invalid-feedback">
                      {t(`${translationPath}.fields.mainDomainName.error`)}
                    </div>
                  )}
                </section>
              </section>
              <section className="box rounded p-3 mb-2">
                <p className="box__description mb-1">
                  {t(`${translationPath}.fields.redirectDomainNames.label`)}
                </p>
                <section className="d-flex flex-column">
                  <FieldArray
                    name="redirectDomainNames"
                    render={arrayHelpers => (
                      <div>
                        {values.redirectDomainNames.map(
                          (_domainName, index) => (
                            <div key={index}>
                              <div className="d-flex mb-1">
                                <Field
                                  name={`redirectDomainNames.${index}`}
                                  render={({
                                    field
                                  }: FieldProps<FormValues>) => (
                                    <TextField
                                      {...field}
                                      inputProps={{
                                        className: "px-2"
                                      }}
                                      className="bg-white w-100"
                                    />
                                  )}
                                />
                                <Button
                                  color="secondary"
                                  variant="contained"
                                  className="ml-2 px-2"
                                  size="small"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  {t(
                                    `${translationPath}.fields.redirectDomainNames.removeFieldButton`
                                  )}
                                </Button>
                              </div>
                              {errors.redirectDomainNames &&
                                touched.redirectDomainNames &&
                                (errors.redirectDomainNames[index] &&
                                  touched.redirectDomainNames[index] && (
                                    <div className="d-flex invalid-feedback mb-1">
                                      {errors.redirectDomainNames[index]}
                                    </div>
                                  ))}
                            </div>
                          )
                        )}
                        <Button
                          color="primary"
                          variant="contained"
                          className="px-2"
                          size="small"
                          disabled={!!errors.redirectDomainNames}
                          onClick={() => arrayHelpers.push("")}
                        >
                          {t(
                            `${translationPath}.fields.redirectDomainNames.addNewFieldButton`
                          )}
                        </Button>
                      </div>
                    )}
                  />
                </section>
              </section>
            </Fragment>,
            <section className="box rounded p-3 mb-2">
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.rewardCafeteriaEnabled.label`)}{" "}
                <small>
                  (
                  {t(
                    `${translationPath}.fields.rewardCafeteriaEnabled.description`
                  )}
                  )
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field name="rewardCafeteriaEnabled">
                  {({ field, form }: FieldProps<FormValues>) => (
                    <Switch
                      value={field.value}
                      onChange={checked =>
                        form.setFieldValue(field.name, checked)
                      }
                    />
                  )}
                </Field>
              </section>
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.workflowsEnabled.label`)}{" "}
                <small>
                  ({t(`${translationPath}.fields.workflowsEnabled.description`)}
                  )
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field name="workflowsEnabled">
                  {({ field, form }: FieldProps<FormValues>) => (
                    <Switch
                      value={field.value}
                      onChange={checked =>
                        form.setFieldValue(field.name, checked)
                      }
                    />
                  )}
                </Field>
              </section>
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.battlesEnabled.label`)}{" "}
                <small>
                  ({t(`${translationPath}.fields.battlesEnabled.description`)})
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field name="featureFlags.battlesEnabled">
                  {({ field, form }: FieldProps<FormValues>) => (
                    <Switch
                      value={field.value}
                      onChange={checked =>
                        form.setFieldValue(field.name, checked)
                      }
                    />
                  )}
                </Field>
              </section>
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.oldAdminEnabled.label`)}{" "}
                <small>
                  ({t(`${translationPath}.fields.oldAdminEnabled.description`)})
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field name="oldAdminEnabled">
                  {({ field, form }: FieldProps<FormValues>) => (
                    <Switch
                      value={field.value}
                      onChange={checked =>
                        form.setFieldValue(field.name, checked)
                      }
                    />
                  )}
                </Field>
              </section>
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.isEnterprise.label`)}{" "}
                <small>
                  ({t(`${translationPath}.fields.isEnterprise.description`)})
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field name="isEnterprise">
                  {({ field, form }: FieldProps<FormValues>) => (
                    <Switch
                      value={field.value}
                      onChange={checked => {
                        form.setFieldValue(field.name, checked);

                        if (checked) {
                          form.setFieldValue("billingEmail", null);
                          form.setFieldValue("billingForename", null);
                          form.setFieldValue("billingSurname", null);
                          form.setFieldTouched("billingEmail", false);
                          form.setFieldTouched("billingForename", false);
                          form.setFieldTouched("billingSurname", false);
                        }
                      }}
                    />
                  )}
                </Field>
                {!values.isEnterprise && (
                  <section className="box rounded pt-3">
                    <p className="box__description mb-1 text-left">
                      {t(`${translationPath}.fields.billingEmail.label`)}{" "}
                      <small>
                        (
                        {t(
                          `${translationPath}.fields.billingEmail.description`
                        )}
                        )
                      </small>
                    </p>
                    <section className="d-flex flex-column">
                      <Field
                        name="billingEmail"
                        render={({ field }: FieldProps<FormValues>) => (
                          <TextField
                            {...field}
                            inputProps={{
                              className: "px-2"
                            }}
                            className={classnames("bg-white m-0", {
                              "is-valid": !errors.billingEmail,
                              "is-invalid":
                                errors.billingEmail && touched.billingEmail
                            })}
                          />
                        )}
                      />
                      {errors.billingEmail && touched.billingEmail && (
                        <div className="d-flex invalid-feedback">
                          {t(`${translationPath}.fields.billingEmail.error`)}
                        </div>
                      )}
                    </section>
                  </section>
                )}
                {!values.isEnterprise && (
                  <section className="box rounded pt-3">
                    <p className="box__description mb-1 text-left">
                      {t(`${translationPath}.fields.billingForename.label`)}{" "}
                      <small>
                        (
                        {t(
                          `${translationPath}.fields.billingForename.description`
                        )}
                        )
                      </small>
                    </p>
                    <section className="d-flex flex-column">
                      <Field
                        name="billingForename"
                        render={({ field }: FieldProps<FormValues>) => (
                          <TextField
                            {...field}
                            inputProps={{
                              className: "px-2"
                            }}
                            className={classnames("bg-white m-0", {
                              "is-valid": !errors.billingForename,
                              "is-invalid":
                                errors.billingForename &&
                                touched.billingForename
                            })}
                          />
                        )}
                      />
                      {errors.billingForename && touched.billingForename && (
                        <div className="d-flex invalid-feedback">
                          {t(`${translationPath}.fields.billingForename.error`)}
                        </div>
                      )}
                    </section>
                  </section>
                )}
                {!values.isEnterprise && (
                  <section className="box rounded pt-3">
                    <p className="box__description mb-1 text-left">
                      {t(`${translationPath}.fields.billingSurname.label`)}{" "}
                      <small>
                        (
                        {t(
                          `${translationPath}.fields.billingSurname.description`
                        )}
                        )
                      </small>
                    </p>
                    <section className="d-flex flex-column">
                      <Field
                        name="billingSurname"
                        render={({ field }: FieldProps<FormValues>) => (
                          <TextField
                            {...field}
                            inputProps={{
                              className: "px-2"
                            }}
                            className={classnames("bg-white m-0", {
                              "is-valid": !errors.billingSurname,
                              "is-invalid":
                                errors.billingSurname && touched.billingSurname
                            })}
                          />
                        )}
                      />
                      {errors.billingSurname && touched.billingSurname && (
                        <div className="d-flex invalid-feedback">
                          {t(`${translationPath}.fields.billingSurname.error`)}
                        </div>
                      )}
                    </section>
                  </section>
                )}
                {!values.isEnterprise && (
                  <section className="box rounded pt-3">
                    <p className="box__description mb-1 text-left">
                      {t(`${translationPath}.fields.currency.label`)}{" "}
                      <small>
                        ({t(`${translationPath}.fields.currency.description`)})
                      </small>
                    </p>
                    <section className="d-flex flex-column">
                      <Field
                        name="currency"
                        render={({ field }: FieldProps<FormValues>) => (
                          <Select
                            id={field.name}
                            value={field.value}
                            onChange={event =>
                              setFieldValue(field.name, event.target.value)
                            }
                          >
                            {["usd", "eur", "pln"].map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.currency && touched.currency && (
                        <div className="d-flex invalid-feedback">
                          {t(`${translationPath}.fields.currency.error`)}
                        </div>
                      )}
                    </section>
                  </section>
                )}
                <section className="box rounded pt-3">
                  <p className="box__description mb-1 text-left">
                    {t(`${translationPath}.fields.hubSpotId.label`)}{" "}
                    <small>
                      ({t(`${translationPath}.fields.hubSpotId.description`)})
                    </small>
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="hubSpotId"
                      render={({ field }: FieldProps<FormValues>) => (
                        <TextField
                          {...field}
                          inputProps={{
                            className: "px-2"
                          }}
                          className={classnames("bg-white m-0", {
                            "is-valid": !errors.hubSpotId,
                            "is-invalid": errors.hubSpotId && touched.hubSpotId
                          })}
                        />
                      )}
                    />
                    {errors.hubSpotId && touched.hubSpotId && (
                      <div className="d-flex invalid-feedback">
                        {t(`${translationPath}.fields.hubSpotId.error`)}
                      </div>
                    )}
                  </section>
                </section>
              </section>
            </section>,
            <section className="box rounded p-3 mb-2">
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.packages.label`)}{" "}
                <small>
                  ({t(`${translationPath}.fields.packages.description`)})
                </small>
              </p>
              <section className="d-flex flex-column">
                <FieldArray
                  name="packages"
                  render={arrayHelpers => (
                    <div>
                      {values.packages.map((_package, index) => (
                        <div key={index}>
                          <div className="d-flex mb-1">
                            <Field
                              name={`packages.${index}`}
                              render={({ field }: FieldProps<FormValues>) => (
                                <TextField
                                  {...field}
                                  inputProps={{
                                    className: "px-2"
                                  }}
                                  className="bg-white w-100"
                                />
                              )}
                            />
                            <Button
                              color="secondary"
                              variant="contained"
                              className="ml-2 px-2"
                              size="small"
                              onClick={() => arrayHelpers.remove(index)}
                            >
                              {t(
                                `${translationPath}.fields.redirectDomainNames.removeFieldButton`
                              )}
                            </Button>
                          </div>
                          {errors.packages &&
                            touched.packages &&
                            (errors.packages[index] &&
                              touched.packages[index] && (
                                <div className="d-flex invalid-feedback mb-1">
                                  {errors.packages[index]}
                                </div>
                              ))}
                        </div>
                      ))}
                      <Button
                        color="primary"
                        variant="contained"
                        className="px-2"
                        size="small"
                        disabled={!!errors.packages}
                        onClick={() => arrayHelpers.push("")}
                      >
                        {t(
                          `${translationPath}.fields.packages.addNewFieldButton`
                        )}
                      </Button>
                    </div>
                  )}
                />
              </section>
            </section>
          ]}
        />
      </div>
    </div>
  </Form>
);

export default withTranslation()(FormComponent);
